import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Package, Truck, Home, PackageOpen, Copy } from 'lucide-react';
import { toast } from 'sonner';
import StoreLayout, { WhatsAppFloat } from '../../components/store/StoreLayout';
import { formatRupee as fmt } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Very simple mapping from item.status → step in the pipeline
const STATUS_STEPS = [
  { key: 'placed', label: 'Order Placed', icon: Package },
  { key: 'dispatched', label: 'Dispatched', icon: Truck },
  { key: 'received', label: 'On the way', icon: PackageOpen },
  { key: 'delivered', label: 'Delivered', icon: Home },
];

const stepIndexFor = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('delivered')) return 3;
  if (s.includes('received in store')) return 2;
  if (s.includes('dispatched')) return 1;
  return 0;
};

export default function OrderTracking() {
  const { token } = useParams();
  const location = useLocation();
  const justPlaced = location.state?.justPlaced;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/store/order/${token}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || 'Order not found'))
      .finally(() => setLoading(false));
  }, [token]);

  const copyLink = () => {
    const url = `${window.location.origin}/order/${token}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Tracking link copied'));
  };

  if (loading) return <StoreLayout><div className="max-w-3xl mx-auto px-4 py-16 text-stone-500">Loading your order…</div></StoreLayout>;
  if (err) return <StoreLayout><div className="max-w-3xl mx-auto px-4 py-16 text-center">
    <div className="text-red-700 mb-2">{err}</div>
    <Link to="/" className="text-amber-800 text-sm">← Go home</Link>
  </div></StoreLayout>;

  return (
    <StoreLayout>
      <WhatsAppFloat />
      <div className="max-w-3xl mx-auto px-4 py-10">
        {justPlaced && (
          <div className="bg-lime-50 border border-lime-200 rounded-xl p-5 mb-8 flex items-start gap-3" data-testid="just-placed-banner">
            <CheckCircle2 className="w-6 h-6 text-lime-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-lime-900">Order placed successfully!</div>
              <div className="text-sm text-lime-800 mt-1">Our team will call you within business hours to confirm delivery + payment.</div>
              <div className="text-xs text-lime-700 mt-2">Bookmark this page or copy the link to check status anytime.</div>
            </div>
          </div>
        )}

        <div className="flex items-baseline justify-between">
          <div>
            <h1 className="text-2xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>Order status</h1>
            <div className="text-xs text-stone-500 font-mono mt-1" data-testid="order-id">{data.order_id}</div>
            <div className="text-xs text-stone-500 mt-1">Placed on {fmtDate(data.order_date)}</div>
          </div>
          <button onClick={copyLink} className="text-xs text-amber-800 hover:underline inline-flex items-center gap-1" data-testid="copy-link">
            <Copy className="w-3.5 h-3.5" /> Copy tracking link
          </button>
        </div>

        <div className="mt-6 bg-stone-50 border border-stone-200 rounded-xl p-5">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Customer</div>
          <div className="font-medium text-stone-900">{data.customer_name || '—'}</div>
        </div>

        {/* Items */}
        <div className="mt-6 space-y-4" data-testid="tracking-items">
          {data.items.map((it, i) => {
            const step = stepIndexFor(it.status);
            return (
              <div key={i} className="border border-stone-200 rounded-xl p-4" data-testid={`tracking-item-${i}`}>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <div className="font-medium text-stone-900">{it.product_name}</div>
                    <div className="text-[11px] text-stone-500">{[it.product_brand, it.product_size, it.product_color].filter(x => x && x !== 'NA').join(' · ')}</div>
                    <div className="text-xs text-stone-500 mt-1">Qty: <span className="text-stone-800">{it.quantity}</span>{it.delivered_qty > 0 && <span> · Delivered: <span className="text-lime-700">{it.delivered_qty}</span></span>}</div>
                  </div>
                  <div className="text-sm font-semibold text-amber-800 tabular-nums">{fmt(it.line_total)}</div>
                </div>
                {/* Steps */}
                <div className="mt-4 flex items-center gap-2">
                  {STATUS_STEPS.map((s, idx) => {
                    const Icon = s.icon;
                    const done = idx <= step;
                    return (
                      <React.Fragment key={s.key}>
                        <div className="flex flex-col items-center flex-1">
                          <div className={`rounded-full w-8 h-8 flex items-center justify-center ${done ? 'bg-amber-700 text-white' : 'bg-stone-200 text-stone-400'}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className={`text-[10px] mt-1 ${done ? 'text-stone-800' : 'text-stone-400'}`}>{s.label}</div>
                        </div>
                        {idx < STATUS_STEPS.length - 1 && <div className={`flex-1 h-0.5 ${idx < step ? 'bg-amber-700' : 'bg-stone-200'}`}></div>}
                      </React.Fragment>
                    );
                  })}
                </div>
                <div className="text-[11px] text-stone-500 mt-3">Current status: <span className="text-stone-800 font-medium">{it.status || '—'}</span></div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex justify-between items-center border-t-2 border-stone-800 pt-4">
          <div className="text-sm text-stone-600">Total (before delivery)</div>
          <div className="text-xl font-semibold text-stone-900 tabular-nums" data-testid="tracking-total">{fmt(data.total_amount)}</div>
        </div>

        <div className="mt-10 text-center text-xs text-stone-500">
          Questions about this order? Reach us on WhatsApp using the button in the bottom-right.
        </div>
      </div>
    </StoreLayout>
  );
}
